import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Permission, resolvePreset } from '@construction/shared';
import { Button, EmptyState, PageHeader, Spinner } from '@/components/ui';
import { LogList } from '@/features/daily-logs/LogList';
import { MyLogsList } from '@/features/daily-logs/MyLogsList';
import { SubmitLogSheet } from '@/features/daily-logs/SubmitLogSheet';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { RangeField } from '@/features/filters/RangeField';
import { RecordTabs } from '@/features/records/RecordTabs';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useRecordTab } from '@/hooks/useRecordTab';
import { usePermissions } from '@/lib/permissions';
import { siteDateKey } from '@/lib/site-time';

/**
 * 日报页（CR-042：一页两个 tab，两套外壳共用）。
 *
 * 客户原话：「日报/我的日报 也可以整合成一样的」。改之前「我的日报」和「日报」是两条路由、
 * 菜单里占两项，既填又看的人要在两页间跳。现在：
 * - 有 `dailyLog.write` → 右上角「写今日日报」+「我的」tab（我在这个工地写过的）；
 * - 有 `dailyLog.view` → 「本工地」tab（全体的，带分页）；
 * - 两个都有才显示 tab 条，**默认「本工地」**（CR-048 推翻 CR-042 的「默认我的」）。
 *
 * **筛选卡片在页面上，两个 tab 共用**（CR-048，客户原话「我的tab里复用 本工地tab的筛选」）：
 * 切 tab 区间不变；下载只在「本工地」出现（下的是全工地的）。
 *
 * CR-082：下载（次按钮）和写日报（主按钮）都在页头；工具条（本工地 / 我的 + 日期胶囊 + 右边汇总）由列表画。
 *
 * 工地只有一个来源（手机首页 / 网页端左侧导航），页面里没有工地下拉（约束 6）。
 * CR-023 之后**没有审核**：日报是分包每天写的流水，超管只看不批。
 */
export function DailyLogsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canWrite = can(Permission.DAILY_LOG_WRITE);
  const canView = can(Permission.DAILY_LOG_VIEW);
  const canExport = can(Permission.DAILY_LOG_EXPORT);

  const { site, isLoading } = useMobileSite();
  const { tab, showTabs, setTab } = useRecordTab({ canMine: canWrite, canSite: canView });

  /**
   * `?compose=1` 直接把写日报的弹层打开（CR-037）。
   *
   * 拍照途中被安卓回收之后，恢复层会把人送回这个地址 —— 送到列表页是不够的，
   * 他填了一半的那张表在弹层里，不自动开的话他看到的还是「什么都没发生」。
   */
  const [params] = useSearchParams();
  const [writing, setWriting] = useState(() => params.get('compose') === '1');

  /**
   * 区间按**工地时区**算「今天」，不按浏览器。
   * 管理员人在别的时区时，用浏览器的今天会让「今日」整体错一天。
   * 默认本周：打开就看得到这几天的，而不是一个空列表。
   */
  const tz = site?.timezone ?? 'America/Los_Angeles';
  const today = siteDateKey(tz);
  const [range, setRange] = useState(() => resolvePreset('thisWeek', today));

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintWorker')} />;
  }

  const toolbar = (
    <>
      {showTabs && <RecordTabs tab={tab} onChange={setTab} />}
      <RangeField
        from={range.from}
        to={range.to}
        today={today}
        presets={['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth']}
        onChange={setRange}
      />
    </>
  );

  return (
    <>
      <PageHeader
        title={t('nav.logs')}
        description={site.name}
        action={
          <>
            {/* 下载的就是眼前这份：同一个区间（CR-043）。「我的」没有下载，下的是全工地的 */}
            {tab === 'site' && canExport && (
              <DownloadButton
                path={`/sites/${site.id}/exports/daily-logs`}
                query={{ from: range.from, to: range.to }}
                label={t('export.download')}
                action="dailyLog.export"
              />
            )}
            {canWrite && (
              <Button onClick={() => setWriting(true)} data-action="logs.write">
                {t('logs.writeToday')}
              </Button>
            )}
          </>
        }
      />

      {tab === 'mine' ? (
        <MyLogsList site={site} from={range.from} to={range.to} toolbar={toolbar} />
      ) : (
        /* key 里带上筛选条件：条件一变就重挂一次，页码自然回到第一页 */
        <LogList
          key={`${range.from}-${range.to}`}
          siteId={site.id}
          timezone={tz}
          from={range.from}
          to={range.to}
          toolbar={toolbar}
        />
      )}

      {writing && <SubmitLogSheet onClose={() => setWriting(false)} />}
    </>
  );
}
